type TabCallback = (tab: Tab) => void;
type TabEventType = 'activate' | 'deactivate';
interface TabEventListner {
    readonly type: TabEventType;
    readonly  callback: TabCallback;
}

class TabGroup {

    private readonly tabs: Tab[] = [];
    private listeners: Array<TabEventListner> = [];

    constructor(
        readonly nav: Element,
        activeTabId?: string,
        listeners?: ReadonlyArray<TabEventListner>
    ) {
        this.nav.querySelectorAll('[data-toggle="tab"][data-target]:not(.disabled)').forEach((tab) => {
            const target = tab.getAttribute('data-target');
            if (target) {
                const contentNode = document.querySelector(target);
                if (contentNode) {
                    this.tabs.push(new Tab(this, tab, contentNode));
                } else {
                    console.warn(`could not find tab content node ${target}`);
                }
            }
        });
        this.listeners = this.listeners.concat(listeners || []);

        // activate given tab if any
        if (activeTabId) {
            const activeTab  = this.tabs.find(tab => tab.id === activeTabId && !tab.active);
            if (activeTab) {
                activeTab.activate();
            }
        }

        // activate first tab if non is active
        if (!this.hasActiveTab() && this.tabs.length > 0) {
            const activeTab = this.tabs[0];
            activeTab.activate();
        }
    }

    findTab(id: string): Tab | undefined {
        return this.tabs.find(t => t.id === id);
    }

    getAllTabs(): Tab[] {
        return this.tabs;
    }

    activated(activated: Tab) {
        this.tabs.forEach((tab) => {
            if (tab !== activated && tab.active) {
                tab.deactivate();
            }
        });
        this.notifyListenersOnActivation(activated);
    }

    deactivated(deactivated: Tab) {
        this.notifyListenersOnDeactivation(deactivated);
    }

    addEventListener(type: 'activate' | 'deactivate', callback: TabCallback) {
        this.listeners.push({type, callback})
    }

    removeEventListner(callback: TabCallback) {
        this.listeners = this.listeners.filter(listener => listener.callback !== callback)
    }

    private hasActiveTab() {
        return this.tabs.find(tab => tab.active) !== undefined;
    }

    private notifyListenersOnActivation(activated: Tab) {
        this.listeners.filter(l => l.type === 'activate').forEach(l => l.callback(activated));
    }

    private notifyListenersOnDeactivation(deactivated: Tab) {
        this.listeners.filter(l => l.type === 'deactivate').forEach(l => l.callback(deactivated));
    }
}

class Tab {

    private onActivate: Function[] = [];
    private onDeactivated: Function[] = [];

    constructor(
        private readonly group: TabGroup,
        private readonly tabElement: Element,
        private readonly contentElement: Element
    ) {
        // adjust pane if needed
        if (this.active) {
            if (!this.contentElement.classList.contains('active')) {
                this.contentElement.classList.add('active');
            }
        } else {
            if (this.contentElement.classList.contains('active')) {
                this.contentElement.classList.remove('active');
            }
        }

        // add event listener
        this.tabElement.addEventListener('click', (event) => {
            event.preventDefault();
            if (event.target instanceof HTMLAnchorElement) {
                // this removes focus from the tab
                event.target.blur();
            }
            if (!this.active) {
                this.activate();
            }
        });
    }

    get id(): string {
        return this.tabElement.getAttribute('data-target')
    }

    get active(): boolean {
        return this.tabElement.classList.contains('active');
    }

    activate() {
        if (!this.active) {
            this.tabElement.classList.add('active');
            this.contentElement.classList.add('active');
            this.group.activated(this);
            this.wasActivated();
        }
    }

    deactivate() {
        if (this.active) {
            this.tabElement.classList.remove('active');
            this.contentElement.classList.remove('active');
            this.group.deactivated(this);
            this.wasDeactivated();
        }
    }

    on(event: 'activate' | 'deactivate', handler: (tab: Tab) => void): Tab {
        switch (event) {
            case 'activate':
                this.onActivate.push(handler);
                if (this.active) {
                    this.wasActivated(handler);
                }
                break;
            case 'deactivate':
                this.onDeactivated.push(handler);
                break;
        }
        return this;
    }

    updateContent(content: string | Element) {
        if (typeof content === 'string') {
            this.contentElement.innerHTML = content;
        } else {
            this.contentElement.innerHTML = '';
            this.contentElement.appendChild(content);
        }
    }

    private wasActivated(newHandler?: Function) {
        this.onActivate.filter(handler => newHandler ? handler === newHandler : true).forEach((fn) => {
            try {
                fn(this);
            } catch (error) {
                console.warn('error in activation handler', error);
            }
        });
    }

    private wasDeactivated() {
        this.onDeactivated.forEach((fn) => {
            try {
                fn(this);
            } catch (error) {
                console.warn('error in deactivation handler', error);
            }
        });
    }
}

const groups: TabGroup[] = [];

export const init = () => {
    const navs = document.querySelectorAll('.nav.nav-tabs');
    if (navs) {
        navs.forEach((nav) => {
            const exists = groups.filter(g => g.nav === nav).length > 0;
            if (!exists) {
                groups.push(new TabGroup(nav, window.location.hash));
            }
        });
    }
};

export const tab = (id: string): Tab => {
    let requestdTab: Tab;
    groups.forEach((group) => {
        group.getAllTabs().forEach((tab) => {
            if (tab.id === id) {
                requestdTab = tab;
            }
        });
    });
    if (!requestdTab) {
        throw new Error(`No tab with ID '${id}' found! Either it is missing or disabled.`);
    } else {
        return requestdTab;
    }
}

export const create = (element: string | Element, activeTab?: string, listeners?: ReadonlyArray<TabEventListner>): TabGroup | undefined => {
    const target = typeof element === 'string' ? document.querySelector(element) : element;
    if (target) {
        return new TabGroup(target, activeTab, listeners);
    } else {
        console.warn('could no find element', {element})
    }
}
